import { ModalLoadFailed } from "@/components/modals";
import { SelectCalculationForm } from "@/forms/select-calculation-form";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import {
	type SelectCalculationFormSchema,
	selectCalculationFormSchema,
} from "@msuite/utils/schemas";
import { generateId } from "@msuite/utils/strings";
import { FormProvider, useForm } from "react-hook-form";

/** Props Interface */
interface AddToCalculationModalProps extends ModalProps {
	onComplete: (calculationId: string) => Promise<void>;
	onOpenCreateCalculation: () => void;
	referenceId: string;
}

export function AddToCalculationModal({
	isOpen,
	onClose,
	referenceId,
	onComplete,
}: AddToCalculationModalProps) {
	/** Hooks */
	const { organization } = useAuth();
	const { t } = useTranslation();
	const methods = useForm({
		resolver: resolver(selectCalculationFormSchema),
	});

	/** Query */
	// The toast stays suppressed because the route answers 404 for a reference
	// whose project cannot be resolved, which is a legitimate "nothing to suggest"
	// and not worth a message. `isError` carries both that and a refused read into
	// the body below, so neither one reaches the user as an empty picker.
	const { data: calculations, isError } = useQuery({
		queryKey: ["calculations", "suggestions", referenceId],
		queryFn: async () => {
			const response = await client.organizations[":orgId"].partnerships[
				":partnershipId"
			].timeline.reference[":referenceId"].bookings.calculation.suggestion.$get({
				param: {
					orgId: organization!.id!,
					partnershipId: generateId(),
					referenceId,
				},
			});
			const data = await responseHandler(response, false);
			return data.serviceCalculations;
		},
		staleTime: 1_000,
	});

	/** Functions */
	async function onSubmit(json: SelectCalculationFormSchema) {
		await onComplete(json.calculationId);
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("calculations.add_to_calculation")}
				onSubmit={methods.handleSubmit(onSubmit)}
			>
				{isError ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<SelectCalculationForm calculations={calculations ?? []} />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
